import { FormProvider, useForm, useWatch } from "react-hook-form";
import { useEffect } from "react";

import FormFieldSpace from "components/FormFields/Space";
import FormFieldTags from "components/FormFields/Tags";
import FormFooterEdit from "components/Forms/FooterEdit";
import Banner from "ds/components/Banner";
import FormField from "ds/components/Form/Field";
import Input from "ds/components/Input";
import Textarea from "ds/components/Textarea";
import useBlockNavigationModal from "hooks/useBlockNavigationModal";
import useTypedContext from "hooks/useTypedContext";
import tooltips from "shared/Scan/tooltips";
import { makeScanUpdateInput } from "shared/Scan/helpers";
import useScanFormOptions from "shared/Scan/useScanFormOptions";
import useScanUpdate from "shared/Scan/useScanUpdate";
import FlashContext from "components/FlashMessages/FlashContext";

import { REFETCH_SCAN_AND_SCAN_SETTINGS_QUERIES } from "../constants";
import { ScanSettingsContextData } from "../Context";
import {
  getFormDefaultValues,
  getIncompatibleSpaceCheck,
  getIncompatibleSpaceMessage,
  ScanSettingsDetailsFormFields,
} from "./helpers";

const ScanSettingsDetailsEdit = () => {
  const { scanSettings } = useTypedContext(ScanSettingsContextData);
  const { reportSuccess } = useTypedContext(FlashContext);

  const [updateScan, { loading }] = useScanUpdate({
    refetchQueries: REFETCH_SCAN_AND_SCAN_SETTINGS_QUERIES,
  });

  const form = useForm<ScanSettingsDetailsFormFields>({
    defaultValues: getFormDefaultValues(scanSettings),
    mode: "onChange",
  });

  const {
    register,
    control,
    handleSubmit,
    formState: { isDirty, errors },
    reset,
  } = form;

  useBlockNavigationModal({ isNavigationBlocked: isDirty });

  const selectedSpace = useWatch({ control, name: "space" });
  const isSpaceChanged = selectedSpace !== scanSettings.space.id;

  const {
    integrations,
    workerPools,
    loading: formOptionsLoading,
    error: formOptionsError,
  } = useScanFormOptions(isSpaceChanged ? selectedSpace : undefined);

  const incompatibleSpaceCheck = getIncompatibleSpaceCheck(scanSettings, isSpaceChanged, {
    integrations,
    workerPools,
    loading: formOptionsLoading,
    error: formOptionsError,
  });
  const { hasIncompatibleIntegration, hasIncompatibleWorkerPool } = incompatibleSpaceCheck;
  const hasIncompatibleSpace = hasIncompatibleIntegration || hasIncompatibleWorkerPool;

  const onSubmit = (formData: ScanSettingsDetailsFormFields) => {
    updateScan(
      { id: scanSettings.id, input: makeScanUpdateInput(scanSettings, formData) },
      (data) => {
        if (data?.scanUpdate) {
          reportSuccess({ message: "Scan was successfully updated" });
        }
      }
    );
  };

  useEffect(() => {
    reset(getFormDefaultValues(scanSettings));
  }, [reset, scanSettings]);

  return (
    <FormProvider {...form}>
      <FormField
        noMargin
        label="Name"
        error={errors?.name?.message}
        tooltipInfo={tooltips.NAME}
        tooltipInfoVariant="modal"
      >
        {({ ariaInputProps }) => (
          <Input
            placeholder="Enter the name"
            error={!!errors?.name}
            {...register("name", { required: "Name field is required." })}
            {...ariaInputProps}
          />
        )}
      </FormField>

      <FormFieldSpace noMargin tooltipInfo={tooltips.SPACE} />
      {hasIncompatibleSpace && (
        <Banner variant="warning" margin="0 0 medium 0">
          {getIncompatibleSpaceMessage(incompatibleSpaceCheck)}
        </Banner>
      )}

      <FormFieldTags
        noMargin
        label="Labels"
        tagName="labels"
        name="labels"
        isOptional
        tooltipInfo={tooltips.LABELS}
        tooltipInfoVariant="modal"
      />

      <FormField
        noMargin
        label="Description"
        isOptional
        tooltipInfo={tooltips.DESCRIPTION}
        tooltipInfoVariant="modal"
      >
        {({ ariaInputProps }) => (
          <Textarea
            placeholder="Describe the purpose of this scan"
            {...register("description")}
            {...ariaInputProps}
          />
        )}
      </FormField>

      <FormFooterEdit
        onSubmit={handleSubmit(onSubmit)}
        onCancel={() => reset(getFormDefaultValues(scanSettings))}
        isDirty={isDirty}
        submitLoading={loading}
        disabled={hasIncompatibleSpace}
        disabledTooltip={getIncompatibleSpaceMessage(incompatibleSpaceCheck)}
      />
    </FormProvider>
  );
};

export default ScanSettingsDetailsEdit;
